import React, { useCallback } from 'react';
import { styled, type SxProps, type Theme } from '@mui/material';
import { type TFunction } from 'i18next';
import { mergeSx } from 'merge-sx';
import * as yup from 'yup';

import { ProcessResponseDTO } from '@/api/generated';
import { FieldPrefixProvider } from '@/hooks/useFieldPrefix';
import { PROCESS_SETTINGS_STEP_KEYS } from '@/root/linker';
import BasicInfoTab from '@/views/ProcessSettingsForm/BasicInfoTab';
import SettingsTab from '@/views/ProcessSettingsForm/SettingsTab/SettingsTab';
import TalentsTab from '@/views/ProcessSettingsForm/TalentsTab';
import TestCatalogTab from '@/views/ProcessSettingsForm/TestCatalogTab';

import LocationTab from './LocationTab';
import { getProcessSettingsBasicInfoValidation } from './schema';

interface ProcessStep {
  key: string;
  title: string;
  content: React.ReactNode;
  optional?: boolean;
  forwardText?: string;
  backText?: string;
  onGoForward?: () => void;
  onGoBack?: () => void;
  footerActions?: React.ReactNode;
  sx?: SxProps<Theme>;
  validation?: yup.Schema;
}

export const getSteps = (
  t: TFunction,
  {
    defaultGoForward,
    defaultGoBack,
    onCreateProcess,
    onSaveSettings,
    onTalentsSave,
    processData,
    testCount = 0,
    processId = null,
    isFullScreenMode = false,
    toggleFullScreenMode = () => {},
    onUpdateProcess = defaultGoForward,
    canAddTalents = false,
    canManageTests = false,
    canUpdateSettings = false
  }: {
    defaultGoForward: () => void;
    defaultGoBack: () => void;
    onCreateProcess: () => void;
    onSaveSettings: () => void;
    onTalentsSave?: () => void;
    testCount?: number;
    processId: string | null;
    processData?: ProcessResponseDTO;
    isFullScreenMode?: boolean;
    toggleFullScreenMode?: (isFullScreen: boolean) => void;
    onUpdateProcess?: () => void;
    canAddTalents?: boolean;
    canManageTests?: boolean;
    canUpdateSettings?: boolean;
  }
): ProcessStep[] =>
  [
    {
      key: 'location',
      title: t('processes:layouts.processCreate.steps.location'),
      content: (
        <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.LOCATION}>
          <LocationTab />
        </FieldPrefixProvider>
      ),
      optional: false,
      onGoForward: processId ? onUpdateProcess : defaultGoForward
    },
    {
      key: 'basicInfo',
      title: t('processes:layouts.processCreate.steps.basicInfo'),
      content: (
        <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.BASIC_INFO}>
          <BasicInfoTab isCreateProcess />
        </FieldPrefixProvider>
      ),
      forwardText: !processId ? t('processes:layouts.processCreate.actions.createAndContinue') : '',
      optional: false,
      validation: getProcessSettingsBasicInfoValidation(t),
      onGoBack: defaultGoBack,
      onGoForward: processId ? onUpdateProcess : onCreateProcess,
      sx: { pt: 3 }
    },
    {
      key: 'tests',
      title: t('processes:layouts.processCreate.steps.tests', { count: testCount }),
      content: processId && (
        <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.TESTS}>
          <TestCatalogTab processId={processId} process={processData} readonly={!canManageTests} />
        </FieldPrefixProvider>
      ),
      optional: false,
      onGoBack: defaultGoBack,
      onGoForward: defaultGoForward,
      sx: { p: 3 }
    },
    canAddTalents && {
      key: 'talents',
      title: t('processes:layouts.processCreate.steps.talents'),
      content: processId && (
        <FieldPrefixProvider prefix={PROCESS_SETTINGS_STEP_KEYS.TALENTS}>
          <TalentsTab processId={processId} process={processData} isCreateFlow showTalentContactInfo />
        </FieldPrefixProvider>
      ),
      forwardText: t('processes:layouts.processCreate.actions.next'),
      optional: true,
      onGoForward: onTalentsSave,
      onGoBack: defaultGoBack,
      sx: { p: 0 }
    },
    {
      key: 'settings',
      title: t('processes:layouts.processCreate.steps.settings'),
      content: processId && (
        <SettingsTabWrapper
          toggleFullScreenMode={toggleFullScreenMode}
          isFullScreenMode={isFullScreenMode}
          processId={processId!}
          canUpdateAccessAndInvitation={canUpdateSettings}
          canUpdateTestProperties={canUpdateSettings}
          canUpdateTalentRegistration={canUpdateSettings}
          canUpdateMessages={canUpdateSettings}
        />
      ),
      forwardText: t('processes:layouts.processCreate.actions.saveAndFinish'),
      optional: true,
      onGoForward: onSaveSettings,
      onGoBack: defaultGoBack,
      sx: { p: 3 }
    }
  ].filter((step: ProcessStep | false): step is ProcessStep => Boolean(step)) as ProcessStep[];

// eslint-disable-next-line react-refresh/only-export-components
const StyledSettingsTab = styled(SettingsTab)(() => ({
  height: 'calc(100% - 100px)',
  '& .CommonFormFooter-root': {
    marginLeft: 0,
    marginRight: 0
  }
}));

// eslint-disable-next-line react-refresh/only-export-components
const SettingsTabWrapper: React.FC<{
  toggleFullScreenMode?: (isFullScreen: boolean) => void;
  isFullScreenMode?: boolean;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  processId?: string;
  canUpdateAccessAndInvitation?: boolean;
  canUpdateTestProperties?: boolean;
  canUpdateTalentRegistration?: boolean;
  canUpdateMessages?: boolean;
}> = ({
  toggleFullScreenMode,
  isFullScreenMode,
  activeTab: externalActiveTab,
  onTabChange: externalOnTabChange,
  processId,
  canUpdateAccessAndInvitation,
  canUpdateTestProperties,
  canUpdateTalentRegistration,
  canUpdateMessages
}) => {
  const [internalActiveTab, setInternalActiveTab] = React.useState(
    externalActiveTab || PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      setInternalActiveTab(tab);
      externalOnTabChange?.(tab);
    },
    [externalOnTabChange]
  );

  return (
    <StyledSettingsTab
      toggleFullScreenMode={toggleFullScreenMode}
      isFullScreenMode={isFullScreenMode}
      activeTab={externalActiveTab || internalActiveTab}
      onTabChange={handleTabChange}
      processId={processId!}
      canUpdateAccessAndInvitation={canUpdateAccessAndInvitation}
      canUpdateTestProperties={canUpdateTestProperties}
      canUpdateTalentRegistration={canUpdateTalentRegistration}
      canUpdateMessages={canUpdateMessages}
      isCreateMode
      sx={mergeSx(
        isFullScreenMode && {
          px: 0,
          height: '100%',
          '& .AddQuestionButton-root': {
            marginInlineEnd: 3
          }
        }
      )}
    />
  );
};
